import * as React from "react";
import "../styles/mailto-contact.css";

/**
 * The one place the public contact address lives. Every user-facing
 * "email us" link/button renders through <MailtoContact>, so changing the
 * address is a single-line edit here.
 */
export const TEAM_EMAIL = "team@sportsacademygames.com";

/** How long the "Copied" confirmation stays up, in ms. */
const COPIED_MS = 2200;

/**
 * A contact-email link that always gives instant feedback.
 *
 * It stays a real `mailto:` anchor — so a visitor who has a mail handler still
 * gets their composer (or webmail tab) just like before — but clicking ALSO
 * copies the address to the clipboard and flashes a brief confirmation. The
 * browser's hand-off to the OS mail handler can take a few seconds, or do
 * nothing at all when no handler is registered; the copy + badge mean the
 * click always does something immediately, so no one is left waiting on it.
 *
 * We deliberately do NOT preventDefault — the mailto must still fire.
 *
 * `children` defaults to the address itself (the inline legal-page links show
 * the address as their text); pass children for a labelled button ("Email us").
 */
export function MailtoContact({
  className,
  children,
}: {
  className?: string;
  children?: React.ReactNode;
}): React.ReactElement {
  const [copied, setCopied] = React.useState(false);
  // window.setTimeout returns a number in the browser; matches the handle type
  // used elsewhere in the portal (LeaveGuard, AuthGate).
  const timerRef = React.useRef<number | undefined>(undefined);

  React.useEffect(() => () => window.clearTimeout(timerRef.current), []);

  function copyAddress() {
    // Don't block the mailto: the anchor's default navigation still runs. If
    // the clipboard is unavailable (insecure context / blocked), we just skip
    // the badge — the mailto is unaffected.
    if (!navigator.clipboard?.writeText) return;
    navigator.clipboard.writeText(TEAM_EMAIL).then(
      () => {
        setCopied(true);
        window.clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(() => setCopied(false), COPIED_MS);
      },
      () => {
        /* copy blocked — the mailto still fired */
      },
    );
  }

  return (
    <a
      className={["mailto-contact", className].filter(Boolean).join(" ")}
      href={`mailto:${TEAM_EMAIL}`}
      onClick={copyAddress}
    >
      {children ?? TEAM_EMAIL}
      {copied && (
        <span className="mailto-contact__copied" role="status" aria-live="polite">
          Copied to clipboard
        </span>
      )}
    </a>
  );
}

/**
 * The quiet escape hatch under a dead end — a sign-in link that won't verify, a
 * code that never arrives, a checkout that won't start.
 *
 * Deliberately understated, and deliberately not the address: emailing us is
 * the slow path, so it must never outrank the retry sitting next to it. Callers
 * gate this on evidence the normal path has already failed (an error, or a
 * resend window elapsed) rather than rendering it upfront.
 *
 * `lead` is the situation-specific opener ("Code never arrived?"). Colors
 * inherit, so it reads correctly on the dark auth panels and the light pages.
 */
export function SupportHint({
  lead = "Still stuck?",
  className,
}: {
  lead?: string;
  className?: string;
}): React.ReactElement {
  return (
    <p className={["support-hint", className].filter(Boolean).join(" ")}>
      {lead} <MailtoContact>Email us</MailtoContact>
    </p>
  );
}
